import { ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ICatalogueSetItem } from "../../../apis/useGetCatalogueData/types";
import { centsToDollarsDisplay } from "../../../common/helpers/stringUtils";
import Condition from "../../../components/Conditional/Condition";
import I18nText from "../../../components/I18nText/I18nText";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import CartItemCustomizations from "./CartItemCustomizations";

interface IProps {
	item: ICatalogueSetItem;
	parentQuantity: number;
}

const CartItemSetItem = ({ item, parentQuantity }: IProps): ReactElement => {
	const { t } = useTranslation();
	const { state: bcrs_deposit_rate } = useConfigContext((state) => state.config.bcrs_deposit_rate);

	const bcrsDepositAmount = useMemo(() => {
		return parentQuantity * item.quantity * (item.bcrs || 0) * bcrs_deposit_rate;
	}, [parentQuantity, item.quantity, item.bcrs, bcrs_deposit_rate]);

	const bcrsDepositText = useMemo(() => {
		const totalBcrsCount = parentQuantity * item.quantity * item.bcrs;

		if (totalBcrsCount > 1) {
			return `${t("cartPage.bcrs.deposit")} × ${totalBcrsCount}`;
		}

		return t("cartPage.bcrs.deposit");
	}, [parentQuantity, item.quantity, item.bcrs, t]);

	return (
		<li className="SetItem">
			<div className="Item">
				<I18nText className="ItemTitle" enText={item.title} cnText={item.title_cn} />
				<Condition check={(item.price_diff ?? 0) > 0}>
					<span className="ItemPrice TabularNums">{`+ $${centsToDollarsDisplay(item.price_diff)}`}</span>
				</Condition>
			</div>
			<Condition check={!!item.food_supplier_customizations?.length}>
				<CartItemCustomizations customizations={item.food_supplier_customizations ?? []} />
			</Condition>
			<Condition check={(item.bcrs || 0) > 0}>
				<div className="SetItemBcrsDeposit">
					<div className="BcrsDepositTitle">{bcrsDepositText}</div>
					<span className="BcrsDepositValue TabularNums">
						+ ${centsToDollarsDisplay(bcrsDepositAmount)}
					</span>
				</div>
			</Condition>
		</li>
	);
};

export default CartItemSetItem;
